import { useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import BatchUpload from "@/components/BatchUpload";
import ReceiptPreview from "@/components/ReceiptPreview";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import TaxYearExport from "@/components/TaxYearExport";
import { useNotifications } from "@/hooks/useNotifications";
import { validateFileType } from "@/lib/validation";
import { normalizeMerchantName } from "@/lib/merchantNormalizer";
import ReceiptFilters from "@/components/ReceiptFilters";
import { deleteReceiptAndSyncSummary, isBusinessExpense as checkIsBusinessExpense, updateFinancialSummary } from "@/lib/receiptUtils";
import { calculateTaxSavings } from "@/lib/taxRules";
import ReceiptScannerFlow from "@/components/receipt-scanner/ReceiptScannerFlow";
import { compressImage } from "@/lib/imageCompression";
import { useReceipts, Receipt } from "@/hooks/useReceipts";
import { UploadProgressAlert, UploadStage } from "@/components/receipts/UploadProgressAlert";
import { BatchUploadProgressAlert } from "@/components/receipts/BatchUploadProgressAlert";
import { UploadCards } from "@/components/receipts/UploadCards";
import { ReceiptGrid } from "@/components/receipts/ReceiptGrid";
import { uploadWithRetry } from "@/lib/uploadRetry";
import { processReceiptsInChunks, ChunkProgress } from "@/lib/batchUploadChunker";

const CONFIDENCE_THRESHOLD = 0.85;

export default function Receipts() {
  const { toast } = useToast();
  const { notify } = useNotifications();
  
  // Use the custom hook for receipts
  const {
    receipts,
    loading,
    loadingMore,
    hasMore,
    filters,
    setFilters,
    resetFilters,
    fetchReceipts,
    refetch
  } = useReceipts();

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState<UploadStage>('idle');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  // Batch upload chunked progress state
  const [batchProgress, setBatchProgress] = useState<ChunkProgress | null>(null);
  
  // UI state
  const [deleting, setDeleting] = useState<string | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [batchUploadOpen, setBatchUploadOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  
  // Review state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewData, setReviewData] = useState<any>(null);
  const [reviewImagePaths, setReviewImagePaths] = useState<string[]>([]);
  const [pendingReceipt, setPendingReceipt] = useState<{
    parsedData: any;
    filePath: string;
    userId: string;
  } | null>(null);

  const hasFilters = Boolean(
    filters.searchQuery || 
    filters.category !== "All Categories" || 
    filters.dateRange || 
    filters.confidenceLevel !== "all" || 
    filters.expenseType !== "all"
  );

  const processFile = async (file: File) => {
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error("File must be less than 10MB");
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      throw new Error("Please upload a JPG, PNG, or PDF file");
    }

    const isValidFileType = await validateFileType(file);
    if (!isValidFileType) {
      throw new Error("File content does not match allowed types");
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    setUploadStage('uploading');
    setUploadProgress(20);

    // Compress image before upload
    const compressedFile = await compressImage(file);
    setUploadProgress(40);

    const fileExt = file.name.split(".").pop();
    const fileName = `${user.id}/${crypto.randomUUID()}.${fileExt}`;
    
    const { error: uploadError } = await supabase.storage
      .from("receipts")
      .upload(fileName, compressedFile);

    if (uploadError) throw uploadError;

    setUploadProgress(60);
    setUploadStage('processing');

    const { data: signedUrlData, error: urlError } = await supabase.storage
      .from("receipts")
      .createSignedUrl(fileName, 3600);

    if (urlError || !signedUrlData) {
      throw new Error("Failed to generate image URL");
    }

    const { data: parseResult, error: parseError } = await supabase.functions.invoke(
      'parse-multi-receipt',
      { body: { filePaths: [fileName] } }
    );

    if (parseError) throw new Error(`Failed to parse receipt: ${parseError.message}`);
    if (!parseResult.success) throw new Error(parseResult.error || "Failed to parse receipt");

    setUploadProgress(90);

    return {
      fileName,
      imageUrl: signedUrlData.signedUrl,
      parsed: parseResult.data,
      userId: user.id,
    };
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';
    setPendingFile(file);
    await processUpload(file);
  };

  const processUpload = async (file: File) => {
    setUploading(true);
    setUploadStage('uploading');
    setUploadProgress(10);
    setUploadError(null);

    try {
      const result = await processFile(file);
      if (!result) {
        setUploading(false);
        setUploadStage('idle');
        return;
      }

      const { fileName, imageUrl, parsed, userId } = result;

      setReviewData(parsed);
      setReviewImagePaths([imageUrl]);
      setPendingReceipt({ parsedData: parsed, filePath: fileName, userId });
      setReviewDialogOpen(true);
      setUploading(false);
      setUploadStage('idle');
      setPendingFile(null);
    } catch (error: any) {
      setUploadError(error.message || "Upload failed");
      setUploadStage('error');
      setUploadProgress(0);
      await notify("receipt_upload_failed", "Upload Failed", error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleRetryUpload = () => {
    if (pendingFile) processUpload(pendingFile);
  };

  const handleReviewConfirm = async (confirmedData: any) => {
    if (!pendingReceipt) return;

    setUploading(true);
    setUploadStage('processing');
    
    try {
      const { userId, filePath } = pendingReceipt;
      const confidence = confirmedData.overallConfidence || 0;

      const transformedData = {
        storeName: confirmedData.storeName,
        date: confirmedData.date,
        currency: confirmedData.currency || 'CAD',
        subtotal: confirmedData.subtotal,
        tax: confirmedData.tax,
        total: confirmedData.total,
        items: confirmedData.items,
        overallConfidence: confidence,
      };

      await saveReceipt({
        userId,
        filePath,
        parsed: transformedData,
        status: confidence >= CONFIDENCE_THRESHOLD ? 'approved' : 'needs_review',
        confidenceScore: confidence,
      });

      setUploadProgress(100);
      setUploadStage('success');

      await notify("receipt_uploaded", "Receipt Saved!", `${transformedData.storeName || "Unknown"} - $${transformedData.total?.toFixed(2) || "0.00"}`);

      setTimeout(() => {
        setUploadStage('idle');
        setUploadProgress(0);
        setReviewDialogOpen(false);
        setReviewData(null);
        setReviewImagePaths([]);
        setPendingReceipt(null);
      }, 2000);

      refetch();
    } catch (error: any) {
      setUploadError(error.message || "Failed to save receipt");
      setUploadStage('error');
      await notify("receipt_upload_failed", "Save Failed", error.message);
    } finally {
      setUploading(false);
    }
  };

  const saveReceipt = async ({
    userId, filePath, parsed, status, confidenceScore, manualTotal
  }: {
    userId: string;
    filePath: string;
    parsed: any;
    status: string;
    confidenceScore: number;
    manualTotal?: number;
  }) => {
    const normalizedMerchantName = normalizeMerchantName(parsed.storeName || parsed.merchant || "Unknown Merchant");
    const finalTotal = manualTotal || parsed.total || 0;
    const finalDate = parsed.date || new Date().toISOString().split("T")[0];
    const isBusinessExpense = parsed.business_expense_assessment?.overall_status !== 'no';
    const taxSavings = parsed.tax_savings_estimate || 0;

    const { error: insertError } = await supabase
      .from("receipts")
      .insert({
        user_id: userId,
        file_path: filePath,
        original_file_path: filePath,
        category: parsed.category || "Other",
        merchant_name: normalizedMerchantName,
        amount: finalTotal,
        subtotal: parsed.subtotal || 0,
        tax: parsed.tax || 0,
        total: finalTotal,
        currency: parsed.currency || "CAD",
        date: finalDate,
        parsed_json: parsed,
        line_items: parsed.items || [],
        status,
        confidence_score: confidenceScore,
        version: 1,
        processing_history: [{
          version: 1,
          timestamp: new Date().toISOString(),
          confidence_score: confidenceScore,
          file_path: filePath,
        }],
      });

    if (insertError) throw insertError;

    await updateFinancialSummary(finalDate, finalTotal, isBusinessExpense, taxSavings, 'add');
  };

  const handleDelete = useCallback(async (id: string) => {
    if (!confirm("Delete this receipt?")) return;
    
    setDeleting(id);
    try {
      await deleteReceiptAndSyncSummary(id);
      toast({ title: "Deleted", description: "Receipt removed" });
      refetch();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setDeleting(null);
    }
  }, [toast, refetch]);

  const handleViewReceipt = useCallback((receipt: Receipt) => {
    setSelectedReceipt(receipt);
    setPreviewDialogOpen(true);
  }, []);

  const handleBatchSubmit = async (batchReceipts: any[]) => {
    setUploading(true);
    setBatchUploadOpen(false);
    setBatchProgress(null);

    toast({ title: "Processing", description: `Uploading ${batchReceipts.length} receipts in chunks...` });

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from('profiles')
        .select('tax_deduction_rate, meal_deduction_rate, currency')
        .eq('id', user.id)
        .single();

      const taxRate = profile?.tax_deduction_rate || 25;
      const mealRate = profile?.meal_deduction_rate || 50;

      // Process in chunks of 5 with progress callback
      const chunkResult = await processReceiptsInChunks(
        batchReceipts,
        user.id,
        5,
        (progress) => {
          setBatchProgress(progress);
          // Update upload progress percentage for the alert
          setUploadProgress(Math.round((progress.processed / progress.total) * 100));
        }
      );

      if (chunkResult.receiptsToInsert.length === 0) {
        throw new Error("All uploads failed. Check your connection and try again.");
      }

      // Build final insert payload
      const receiptsToInsert = chunkResult.receiptsToInsert.map(({ receipt, fileName }) => {
        const normalizedMerchantName = normalizeMerchantName(receipt.merchant);
        const taxSavings = calculateTaxSavings(receipt.total, receipt.category, taxRate, mealRate);
        const isBusinessCategory = checkIsBusinessExpense(receipt.category);

        return {
          file_path: fileName,
          merchant_name: normalizedMerchantName,
          date: receipt.date,
          amount: receipt.total,
          total: receipt.total,
          subtotal: receipt.total,
          tax: 0,
          category: receipt.category,
          currency: profile?.currency || 'CAD',
          tax_savings_estimate: taxSavings,
          confidence_score: 1.0,
          parsed_json: { business_expense_assessment: { overall_status: isBusinessCategory ? 'yes' : 'no' } },
          line_items: [],
        };
      });

      const { data: result, error: batchError } = await supabase.rpc('batch_insert_receipts', {
        p_receipts: receiptsToInsert,
        p_user_id: user.id
      });

      if (batchError) throw batchError;

      const successCount = (result as any)?.success_count || receiptsToInsert.length;
      toast({ title: "Success!", description: `${successCount} receipts added` });

      if (chunkResult.failedCount > 0) {
        toast({
          title: "Some receipts were skipped",
          description: `${chunkResult.failedCount} file(s) failed to upload — try again on Wi‑Fi or upload fewer at a time.`,
          variant: "destructive",
        });
      }
      refetch();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setUploading(false);
      setBatchProgress(null);
      setUploadProgress(0);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Receipts</h2>
          <p className="text-muted-foreground text-sm">Manage your expense receipts</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setExportDialogOpen(true)}>
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
      </div>

      {uploadStage !== 'idle' && (
        <Card className="p-4">
          <UploadProgressAlert
            stage={uploadStage}
            progress={uploadProgress}
            error={uploadError}
            onRetry={handleRetryUpload}
          />
        </Card>
      )}

      {/* Batch upload chunked progress */}
      {batchProgress && (
        <Card className="p-4">
          <BatchUploadProgressAlert progress={batchProgress} uploading={uploading} />
        </Card>
      )}

      <UploadCards
        uploading={uploading}
        onFileChange={handleUpload}
        onBatchOpen={() => setBatchUploadOpen(true)}
      />

      <ReceiptFilters 
        filters={filters} 
        onFilterChange={setFilters}
        onReset={resetFilters}
      />

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Recent Receipts</h3>
        <ReceiptGrid
          receipts={receipts}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          hasFilters={hasFilters}
          deletingId={deleting}
          onDelete={handleDelete}
          onView={handleViewReceipt}
          onLoadMore={() => fetchReceipts(false)}
        />
      </div>

      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedReceipt && (
            <ReceiptPreview
              filePath={selectedReceipt.file_path}
              confidenceScore={selectedReceipt.confidence_score || 0}
              confidenceRegions={selectedReceipt.confidence_regions}
              onClose={() => setPreviewDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <BatchUpload
        open={batchUploadOpen}
        onClose={() => setBatchUploadOpen(false)}
        onSubmit={handleBatchSubmit}
      />

      <TaxYearExport
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
      />

      {reviewData && reviewImagePaths.length > 0 && (
        <ReceiptScannerFlow
          open={reviewDialogOpen}
          onClose={() => {
            setReviewDialogOpen(false);
            setReviewData(null);
            setReviewImagePaths([]);
            setPendingReceipt(null);
          }}
          onComplete={(_, confirmedData) => {
            if (confirmedData) handleReviewConfirm(confirmedData);
          }}
          extractedData={reviewData}
          capturedImages={reviewImagePaths}
        />
      )}
    </div>
  );
}
